import { useState } from "react";
import { Loader2 } from "lucide-react";

import ConvocationMicrosoftSignIn from "@/components/ConvocationMicrosoftSignIn";
import { API_BASE } from "@/lib/api";
import { setStudentSession } from "@/lib/studentAuth";

export default function StudentPortalSignIn({
  error,
  authRequired,
  onSignedIn,
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  const otpEnabled = authRequired?.otp_enabled !== false;

  const requestOtp = async () => {
    setLocalError(null);
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/public/otp/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || "Could not send code");
      setOtpSent(true);
    } catch (e) {
      setLocalError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setLocalError(null);
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/public/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim() }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || "Verification failed");
      setStudentSession({
        token: body.token,
        expires_at: body.expires_at,
        email: body.email,
      });
      onSignedIn?.();
    } catch (e) {
      setLocalError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const displayError = localError || error;

  return (
    <ConvocationMicrosoftSignIn
      title="Convocation 2026"
      subtitle="Sign in to access your convocation details"
      description="Enter your university email (@mahindrauniversity.edu.in) to receive a one-time sign-in code."
      error={displayError}
      hideMicrosoftButton
      testId="convocation-portal-signin"
      footer={(
        <div className="mt-6">
          {otpEnabled ? (
            <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-4 text-left">
              <p className="text-sm font-semibold text-[#111]">Sign in with email code</p>
              <p className="text-xs text-[#6B7280] mt-1">Code is sent to your university email in users.mail_id.</p>
              <label className="block mt-4 text-xs font-medium text-[#6B7280]" htmlFor="portal-email">
                University email
              </label>
              <input
                id="portal-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mahindrauniversity.edu.in"
                className="mt-1 w-full h-11 rounded-lg border border-[#E5E7EB] px-3 text-sm"
                data-testid="portal-email-input"
              />
              {!otpSent ? (
                <button
                  type="button"
                  disabled={busy || !email.trim()}
                  onClick={requestOtp}
                  className="mt-3 w-full h-11 rounded-lg bg-[#B60F2A] text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="portal-request-otp"
                >
                  {busy ? "Sending…" : "Send sign-in code"}
                </button>
              ) : (
                <>
                  <label className="block mt-4 text-xs font-medium text-[#6B7280]" htmlFor="portal-code">
                    6-digit code
                  </label>
                  <input
                    id="portal-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="mt-1 w-full h-11 rounded-lg border border-[#E5E7EB] px-3 text-sm tracking-[0.35em]"
                    data-testid="portal-otp-input"
                  />
                  <button
                    type="button"
                    disabled={busy || code.length !== 6}
                    onClick={verifyOtp}
                    className="mt-3 w-full h-11 rounded-lg bg-[#B60F2A] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="portal-verify-otp"
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Verify and continue
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={requestOtp}
                    className="mt-2 w-full text-xs text-[#6B7280] hover:text-[#111]"
                    data-testid="portal-resend-otp"
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => { setOtpSent(false); setCode(""); setLocalError(null); }}
                    className="mt-2 w-full text-xs text-[#6B7280] hover:text-[#111]"
                  >
                    Use a different email
                  </button>
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#6B7280] text-center">
              Email sign-in is not available right now. Please contact the convocation team.
            </p>
          )}
        </div>
      )}
    />
  );
}
